import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import {
  UnsupportedBrowserModalComponent,
  UnsupportedBrowserPopup
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { PushPipe } from '@ngrx/component';
import { UnsupportedBrowserFacadeService } from '../../data-access/facades/unsupported-browser/unsupported-browser-facade.service';
import { CookieHelperService } from '../../data-access/services/utils';

export const BROWSER_WARNING_COOKIE = 'browserWarning';

@Component({
  selector: 'idp-unsupported-browser',
  standalone: true,
  imports: [CommonModule, UnsupportedBrowserModalComponent, PushPipe],
  templateUrl: './unsupported-browser.component.html',
  styleUrls: ['./unsupported-browser.component.scss']
})
export class UnsupportedBrowserComponent implements OnInit, AfterViewInit {
  facade = inject(UnsupportedBrowserFacadeService);
  platformId = inject(PLATFORM_ID);
  cookieHelper = inject(CookieHelperService);
  showModal = false;
  emptyUnsupportedBrowserPopup: UnsupportedBrowserPopup =
    {} as UnsupportedBrowserPopup;

  ngOnInit(): void {
    this.facade.init();
  }

  ngAfterViewInit() {
    if (this.isBadBrowser()) {
      this.showModal = true;
      this.facade.setBadBrowser(BROWSER_WARNING_COOKIE, 1); // migrated from legacy code to avoid breaking changes
    }
  }

  unSupportedBrowserClose() {
    this.showModal = false;
  }

  isBadBrowser(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const ua = navigator.userAgent;
    if (!ua) return false;
    return this.isUnsupportedBrowser(ua);
  }

  private isUnsupportedBrowser(ua: string): boolean {
    if (this.isIE(ua)) return true;
    if (this.isFlyDeltaApp(ua)) return this.isOldFlyDeltaVersion(ua);
    if (this.isIOSDevice(ua)) return this.isOldIOSBrowser(ua);
    return this.isOldDesktopOrAndroidBrowser(ua);
  }

  isFlyDeltaApp(ua: string): boolean {
    if (ua.includes('FlyDelta') || ua.includes('Fly Delta')) return true;
    return this.isWebView(ua);
  }

  private isWebView(ua: string): boolean {
    const isIOSWebView =
      this.isIOSDevice(ua) &&
      ua.includes('AppleWebKit') &&
      !ua.includes('Safari');
    return isIOSWebView || /; wv/.test(ua);
  }

  isOldFlyDeltaVersion(ua: string): boolean {
    if (ua.includes('iOS')) return this.isOldIOSVersion(ua);
    if (ua.includes('Android')) return false;
    return false;
  }

  isOldIOSVersion(ua: string): boolean {
    const iosVersion = this.extractIOSVersion(ua);
    return iosVersion > 0 && iosVersion < 13.4;
  }

  extractIOSVersion(ua: string): number {
    const match = ua.match(/(?:OS |iOS\/)(\d+)[_.](\d+)/);
    if (!match) return 0;
    return parseFloat(`${match[1]}.${match[2]}`);
  }

  private isOldIOSBrowser(ua: string): boolean {
    if (ua.includes('CriOS'))
      return this.extractVersion(ua, /CriOS\/(\d+)/) < 79;
    if (ua.includes('FxiOS'))
      return this.extractVersion(ua, /FxiOS\/(\d+)/) < 78;
    if (ua.includes('EdgiOS'))
      return this.extractVersion(ua, /EdgiOS\/(\d+)/) < 79;
    return this.extractVersion(ua, /Version\/(\d+\.?\d*)/) < 12.1;
  }

  private isOldDesktopOrAndroidBrowser(ua: string): boolean {
    if (this.isChrome(ua)) return this.extractVersion(ua, /Chrome\/(\d+)/) < 79;
    if (this.isEdge(ua)) return this.extractVersion(ua, /Edg[A]?\/(\d+)/) < 79;
    return this.isOldFirefoxOrSafari(ua);
  }

  private isOldFirefoxOrSafari(ua: string): boolean {
    if (this.isFirefox(ua))
      return this.extractVersion(ua, /Firefox\/(\d+)/) < 78;
    if (this.isSafari(ua))
      return this.extractVersion(ua, /Version\/(\d+\.?\d*)/) < 12.1;
    return false;
  }

  private isIOSDevice(ua: string): boolean {
    return /iPhone|iPad|iPod/.test(ua);
  }

  private isIE(ua: string): boolean {
    return ua.includes('MSIE') || ua.includes('Trident/');
  }

  private isChrome(ua: string): boolean {
    return ua.includes('Chrome') && !ua.includes('Edg');
  }

  private isEdge(ua: string): boolean {
    return ua.includes('Edg/') || ua.includes('EdgA/');
  }

  private isFirefox(ua: string): boolean {
    return ua.includes('Firefox') && !ua.includes('Seamonkey');
  }

  private isSafari(ua: string): boolean {
    const hasSafari = ua.includes('Safari');
    return hasSafari && !ua.includes('Chrome') && !ua.includes('Firefox');
  }

  private extractVersion(ua: string, regex: RegExp): number {
    const match = ua.match(regex);
    return match ? parseFloat(match[1]) : 0;
  }
}
